import type {
  ChannelRecord,
  CreateChannelRequest,
  CreateChannelResponse,
  DeleteChannelResponse,
  UpdateChannelRequest,
  UpdateChannelResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { createChannel, deleteChannel, listChannels, updateChannel } from './channels.ts';

const CHANNELS_KEY = ['channels'] as const;

export function useChannelsList(): UseQueryResult<readonly ChannelRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...CHANNELS_KEY],
    queryFn: async () => {
      const res = await listChannels(fetchImpl);
      return res.channels;
    },
  });
}

export function useCreateChannel(): UseMutationResult<CreateChannelResponse, Error, CreateChannelRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateChannelRequest) => createChannel(body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CHANNELS_KEY }); },
  });
}

export function useUpdateChannel(): UseMutationResult<UpdateChannelResponse, Error, { id: string; body: UpdateChannelRequest }> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => updateChannel(id, body, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CHANNELS_KEY }); },
  });
}

export function useDeleteChannel(): UseMutationResult<DeleteChannelResponse, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteChannel(id, fetchImpl),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: CHANNELS_KEY }); },
  });
}
